import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { ShieldCheck } from "lucide-react";
import { nginxProvisionWildcard } from "../../redux/actionCreator";

/**
 * Issue one `*.domain` certificate covering every subdomain at that level.
 *
 * Only possible over DNS-01, which this server is already configured for
 * (HTTP-01 cannot prove control of a wildcard). Once issued, adding a
 * subdomain is a single file write — Add Subdomain offers the existing
 * certificate and skips certbot entirely.
 */
export default function WildcardCertModal({ show, onHide, projectId, serverId, onDone }) {
  const dispatch = useDispatch();
  const [domain, setDomain] = useState("");
  const [includeApex, setIncludeApex] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const base = domain.trim().replace(/^\*\./, "").toLowerCase();
  const valid = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(base);

  const run = async () => {
    setBusy(true);
    setResult(null);
    const res = await dispatch(
      nginxProvisionWildcard(projectId, serverId, { domain: base, include_apex: includeApex }),
    );
    setBusy(false);
    setResult(res);
    if (res?.status) onDone && onDone();
  };

  const close = () => { if (busy) return; setResult(null); onHide && onHide(); };

  return (
    <Modal show={show} onHide={close} centered backdrop="static">
      <Modal.Header closeButton={!busy}>
        <h6 className="m-0 d-inline-flex align-items-center gap-2">
          <ShieldCheck size={16} /> Wildcard certificate
        </h6>
      </Modal.Header>
      <Modal.Body>
        {!result ? (
          <>
            <Form.Label>Domain</Form.Label>
            <Form.Control
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              disabled={busy}
              style={{ fontFamily: "ui-monospace, monospace" }}
            />
            <small className="text-muted d-block mt-1">
              Issues <code>*.{base || "example.com"}</code>
              {includeApex && <> and <code>{base || "example.com"}</code></>}.
            </small>
            <Form.Check
              className="mt-2"
              type="switch"
              id="wc-apex"
              checked={includeApex}
              disabled={busy}
              onChange={(e) => setIncludeApex(e.target.checked)}
              label={<span className="small">Also cover the bare domain</span>}
            />
            <div className="small text-muted mt-2">
              A wildcard matches exactly one level: <code>api.{base || "example.com"}</code> is
              covered, <code>a.b.{base || "example.com"}</code> is not, and the bare
              domain needs the switch above. Uses Cloudflare DNS-01 with the
              credentials and email set on this server, and waits for DNS to
              propagate — this usually takes a minute.
            </div>
          </>
        ) : (
          <div className={`alert ${result.status ? "alert-success" : "alert-danger"} mb-0`}>
            <strong>{result.message}</strong>
            {result.data?.covers && (
              <div className="small mt-1">Covers: {result.data.covers.map((c) => <code key={c} className="me-2">{c}</code>)}</div>
            )}
            {(result.data?.output) && (
              <pre className="small mt-2 mb-0" style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>
                {result.data.output}
              </pre>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={close} disabled={busy}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button variant="primary" onClick={run} disabled={busy || !valid}>
            {busy ? <><Spinner size="sm" animation="border" /> Issuing…</> : "Issue wildcard"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
